import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ArchivedItemsPanel = ({ items, authHeaders, onRefresh, isOwner }) => {
  const [deleteTarget, setDeleteTarget] = useState(null);
  const archivedItems = items.filter((i) => !i.active);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/inventory/items/${deleteTarget.id}`, { headers: authHeaders });
      toast.success("Item permanently deleted");
      setDeleteTarget(null);
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete item");
      setDeleteTarget(null);
    }
  };

  return (
    <div data-testid="archived-items-panel" className="space-y-4">
      <p className="text-sm text-slate-500">
        Hidden from your active list and Shopping List. Use "+ Add Item" on the Master Item List tab with the
        same name to bring one back as a fresh item — it can't be reactivated directly. Deleting is permanent
        and takes its history with it.
      </p>
      <div className="border border-slate-200 rounded-xl divide-y divide-slate-100">
        {archivedItems.length === 0 ? (
          <p className="text-sm text-slate-400 py-10 text-center">No archived items.</p>
        ) : archivedItems.map((item) => {
          const mergedIntoName = item.mergedIntoItemId ? items.find((i) => i.id === item.mergedIntoItemId)?.canonicalName : null;
          return (
            <div key={item.id} data-testid={`archived-row-${item.id}`} className="flex items-center justify-between px-4 py-3">
              <div>
                <p className="font-medium text-slate-700">{item.canonicalName}</p>
                <p className="text-xs text-slate-400">{mergedIntoName ? `Merged into "${mergedIntoName}"` : "Archived"}</p>
              </div>
              {isOwner && (
                <button data-testid={`delete-archived-${item.id}`} title="Permanently delete" onClick={() => setDeleteTarget(item)}>
                  <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-600" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete "{deleteTarget?.canonicalName}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This can't be undone. All of its history — past purchase counts and shopping list totals tied to
              this item — goes with it. If you just want it gone from view, Archive is enough; this removes it for good.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-ok" onClick={confirmDelete}>Delete Permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ArchivedItemsPanel;
